import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  Eye,
  Edit3,
  Save,
  FileSpreadsheet,
  ZoomIn,
  ZoomOut,
  Printer,
  Undo,
  Redo,
  LayoutTemplate
} from 'lucide-react';
import { 
  db, 
  collection, 
  onSnapshot, 
  query, 
  where,
  setDoc, 
  updateDoc,
  deleteDoc,
  doc, 
  serverTimestamp, 
  OperationType, 
  handleFirestoreError 
} from '../firebase';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { geminiService } from '../services/geminiService';
import { StateContractsDatabaseSection } from './StateContractsDatabaseSection';
import { HistoricalBidsAnalysisSection } from './HistoricalBidsAnalysisSection';
import { SgpvpRetrievalDashboard } from './SgpvpRetrievalDashboard';
import { CentralStateSolicitationsSection } from './CentralStateSolicitationsSection';
import { UserRfxItem } from '../services/centralRfxService';
import { MultiSelectCombo } from './MultiSelectCombo';
import { generateProposalPdf } from '../utils/proposalPdf';
import { motion, AnimatePresence } from 'motion/react';
import { getSafeSolicitationDocId, ensureFirestoreSafeSize } from '../utils/firestoreSanitizer';
import { US_STATES } from '../utils/states';
import { ComprehensiveProposalStudioModal } from './ComprehensiveProposalStudioModal';
import { 
  Database, 
  Search, 
  Filter, 
  Grid, 
  List, 
  Plus, 
  RefreshCw, 
  X, 
  ChevronRight, 
  ChevronLeft, 
  MapPin, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Activity, 
  ExternalLink, 
  Bookmark, 
  BookmarkCheck, 
  Award, 
  Sparkles, 
  FileText, 
  Check, 
  AlertTriangle, 
  Play, 
  Terminal, 
  HelpCircle,
  Building,
  Upload,
  Paperclip,
  Trash2,
  File
} from 'lucide-react';

interface RFXDetail {
  generalinformation?: any;
  response?: {
    rows?: Array<Record<string, string>>;
    [key: string]: any;
  };
  solicitationdocuments?: Array<any>;
}

interface RFXOpportunity {
  solicitationId: string;
  title: string;
  state: string;
  agency: string;
  description: string;
  dueDate: string;
  status: 'open' | 'closed' | 'awarded';
  category: string;
  sourceUrl?: string;
  importSource?: string;
  pricing?: number;
  details?: RFXDetail | null;
  createdAt?: any;
}

const cn = (...classes: any[]) => classes.filter(Boolean).join(' ');

export const RFXDatabaseView: React.FC = () => {
  const { user, vendorProfile } = useAuth();

  const getBrandFontClass = (font?: string | null) => {
    switch (font?.toLowerCase()) {
      case 'space grotesk':
      case 'grotesk':
        return 'font-grotesk';
      case 'playfair display':
      case 'serif':
        return 'font-serif';
      case 'jetbrains mono':
      case 'mono':
        return 'font-mono';
      default:
        return 'font-sans';
    }
  };
  const { savedList, toggleSave, isSaved, addToast } = useSavedSolicitations();

  // Core Data States
  const [opportunities, setOpportunities] = useState<RFXOpportunity[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedBudgets, setSelectedBudgets] = useState<string[]>([]); // 'Under 50k', '50k-250k', 'Over 250k'

  // View Layout State
  const [registrySource, setRegistrySource] = useState<'all' | 'federal' | 'state' | 'municipal' | 'watchlist'>('all');
  const [activeSubTab, setActiveSubTab] = useState<'opportunities' | 'central-rfx' | 'contracts' | 'historical-rfx' | 'sgpvp'>('central-rfx');
  const [userParticipatedIds, setUserParticipatedIds] = useState<Set<string>>(new Set());

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Drawer / Modal States
  const [selectedRFX, setSelectedRFX] = useState<RFXOpportunity | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isComprehensiveModalOpen, setIsComprehensiveModalOpen] = useState(false);
  const [targetComprehensiveRFX, setTargetComprehensiveRFX] = useState<RFXOpportunity | null>(null);

  // Gemini Integration States inside Detail Panel
  const [analyzingFit, setAnalyzingFit] = useState(false);
  const [fitResult, setFitResult] = useState<any>(null);
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [responseProgress, setResponseProgress] = useState<string>('');
  const [currentBid, setCurrentBid] = useState<any>(null);
  const [modalTab, setModalTab] = useState<'details' | 'response_documents'>('details');

  // Sync / Subscribe to active bid in Firestore when selectedRFX changes
  useEffect(() => {
    if (!selectedRFX || !user) {
      setCurrentBid(null);
      setFitResult(null);
      setModalTab('details');
      return;
    }
    const bidId = `${user.uid}_${selectedRFX.solicitationId}`;
    const unsubscribe = onSnapshot(doc(db, 'bids', bidId), (docSnap) => {
      if (docSnap.exists()) {
        const bidData = docSnap.data();
        setCurrentBid(bidData);
        if (bidData.analysis) {
          setFitResult(bidData.analysis);
        }
      } else {
        setCurrentBid(null);
      }
    }, (err) => {
      console.error("Error subscribing to bid inside database view:", err);
    });
    return () => unsubscribe();
  }, [selectedRFX, user]);

  const [editedProposal, setEditedProposal] = useState<string>('');
  const [sowMode, setSowMode] = useState<'preview' | 'edit'>('preview');
  const [activePreviewDoc, setActivePreviewDoc] = useState<'sow' | 'spreadsheet'>('sow');
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number; field: string } | null>({ row: 0, col: 4, field: 'price' });
  const [spreadsheetRows, setSpreadsheetRows] = useState<any[]>([]);

  useEffect(() => {
    if (selectedRFX) {
      const rows = selectedRFX.details?.response?.rows || [];
      if (rows.length > 0) {
        setSpreadsheetRows(rows.map((row, idx) => {
          const rawQty = parseInt(row['10'] || row['Qty']) || 1;
          const initialPrice = 150 + idx * 45;
          return {
            line: row['2'] || row['0'] || (idx + 1).toString(),
            item: row['5'] || 'Deliverable Service Item',
            qty: rawQty,
            unit: row['11'] || 'Lot',
            price: initialPrice,
            total: rawQty * initialPrice
          };
        }));
      } else {
        setSpreadsheetRows([
          { line: "1.1", item: "Senior Enterprise Software Architect", qty: 160, unit: "Hours", price: 175, total: 28000 },
          { line: "1.2", item: "Senior Cloud & Security Systems Engineer", qty: 240, unit: "Hours", price: 150, total: 36000 },
          { line: "1.3", item: "Data Integration & Compliance Consultant", qty: 120, unit: "Hours", price: 185, total: 22200 },
          { line: "1.4", item: "Full-Stack Deployment & QA Specialist", qty: 180, unit: "Hours", price: 125, total: 22500 },
          { line: "2.1", item: "SaaS Secure Gov-Cloud Node License (Annual)", qty: 2, unit: "Each", price: 12500, total: 25000 },
          { line: "2.2", item: "Gemini Real-Time SOW API Credits (Million Tokens)", qty: 15, unit: "Each", price: 350, total: 5250 },
          { line: "3.1", item: "Post-Implementation Support & Maintenance (L1/L2)", qty: 1, unit: "Year", price: 18000, total: 18000 }
        ]);
      }
      setActivePreviewDoc('sow');
      setSowMode('preview');
    } else {
      setSpreadsheetRows([]);
    }
  }, [selectedRFX]);

  useEffect(() => {
    if (currentBid?.generatedContent) {
      setEditedProposal(currentBid.generatedContent);
    } else {
      setEditedProposal('');
    }
  }, [currentBid?.generatedContent]);

  // Form Input States
  const [formId, setFormId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formAgency, setFormAgency] = useState('');
  const [formState, setFormState] = useState('TX');
  const [formCategory, setFormCategory] = useState('Information Technology');
  const [formBudget, setFormBudget] = useState('150000');
  const [formDueDate, setFormDueDate] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formSourceUrl, setFormSourceUrl] = useState('');
  const [formFiles, setFormFiles] = useState<Array<{ name: string; size: number; type: string; dataUrl?: string }>>([]);

  const handleFormFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const filesArray = Array.from(e.target.files);
    filesArray.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setFormFiles(prev => [
          ...prev,
          {
            name: file.name,
            size: file.size,
            type: file.type || 'application/octet-stream',
            dataUrl: reader.result as string
          }
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleRemoveFormFile = (index: number) => {
    setFormFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 1. Subscribe to Live Firestore RFX Opportunities (User's Participated Items + Global Records)
  useEffect(() => {
    setLoading(true);
    let unsubUser = () => {};
    let unsubGlobal = () => {};

    const userItemsMap = new Map<string, RFXOpportunity>();
    const globalItemsMap = new Map<string, RFXOpportunity>();

    const mergeAndSet = () => {
      const combined = new Map<string, RFXOpportunity>();
      globalItemsMap.forEach((item, id) => combined.set(id, item));
      userItemsMap.forEach((item, id) => combined.set(id, item));

      const sortedData = Array.from(combined.values()).sort((a, b) => {
        if (a.status === 'open' && b.status !== 'open') return -1;
        if (a.status !== 'open' && b.status === 'open') return 1;
        return b.solicitationId.localeCompare(a.solicitationId);
      });

      setOpportunities(sortedData);
      setUserParticipatedIds(new Set(Array.from(userItemsMap.keys())));
      setLoading(false);
    };

    if (user?.uid) {
      const qUser = query(collection(db, 'user_rfx_items'), where('userId', '==', user.uid));
      unsubUser = onSnapshot(qUser, (snapshot) => {
        userItemsMap.clear();
        snapshot.docs.forEach(docSnap => {
          const docData = docSnap.data();
          const sId = docData.solicitationId || docSnap.id.replace(`${user.uid}_`, '');
          userItemsMap.set(sId, {
            solicitationId: sId,
            title: docData.title || 'Untitled RFX',
            state: docData.state || docData.stateCode || 'N/A',
            agency: docData.agency || 'State Agency',
            description: docData.description || 'No description provided.',
            dueDate: docData.dueDate || '',
            status: docData.status || 'open',
            category: docData.category || 'Procurement Services',
            sourceUrl: docData.sourceUrl || '',
            importSource: docData.importSource || 'User Participated (Copied)',
            pricing: docData.pricing || 75000,
            details: docData.details || null,
            createdAt: docData.createdAt || docData.copiedFromCentralAt
          } as RFXOpportunity);
        });
        mergeAndSet();
      }, (err) => {
        console.warn('user_rfx_items subscription note:', err);
        mergeAndSet();
      });
    }

    const q = query(collection(db, 'solicitations'));
    unsubGlobal = onSnapshot(q, (snapshot) => {
      globalItemsMap.clear();
      snapshot.docs.forEach(docSnap => {
        const docData = docSnap.data();
        globalItemsMap.set(docSnap.id, {
          solicitationId: docSnap.id,
          title: docData.title || 'Untitled RFX',
          state: docData.state || 'N/A',
          agency: docData.agency || docData.buyingagency || 'State Portal',
          description: docData.description || docData.maincommodity || 'No description provided.',
          dueDate: docData.dueDate || docData.dueclosedate || '',
          status: docData.status || 'open',
          category: docData.category || docData.maincommodity || 'Procurement Services',
          sourceUrl: docData.sourceUrl || docData.rfxitempageurl || '',
          importSource: docData.importSource || '',
          pricing: docData.pricing || (docData.details?.generalinformation?.estimatedBudget ? parseFloat(docData.details.generalinformation.estimatedBudget.replace(/[^0-9.]/g, '')) : 75000),
          details: docData.details || null,
          createdAt: docData.createdAt
        } as RFXOpportunity);
      });
      mergeAndSet();
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'solicitations');
      mergeAndSet();
    });

    return () => {
      unsubUser();
      unsubGlobal();
    };
  }, [user]);

  // 2. Derive Filters & Options Dynamic List
  const stateOptions = useMemo(() => {
    const states = opportunities.map(o => o.state).filter(Boolean);
    return ['All', ...Array.from(new Set(states))].sort();
  }, [opportunities]);

  const mappedStateOptions = useMemo(() => {
    return stateOptions.filter(s => s !== 'All').map(s => ({ value: s, label: s }));
  }, [stateOptions]);

  const budgetOptions = useMemo(() => [
    { value: 'Under 50k', label: 'Under $50k' },
    { value: '50k-250k', label: '$50k - $250k' },
    { value: 'Over 250k', label: 'Over $250k' }
  ], []);

  const statusOptions = useMemo(() => [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed / Past' },
    { value: 'awarded', label: 'Awarded' }
  ], []);

  const categoryOptions = useMemo(() => {
    const cats = opportunities.map(o => o.category).filter(Boolean);
    return ['All', ...Array.from(new Set(cats))].slice(0, 15).sort();
  }, [opportunities]);

  // 3. Filter Logic
  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(item => {
      if (!item) return false;
      const solicitationId = item.solicitationId || '';
      const title = item.title || '';
      const agency = item.agency || '';
      const description = item.description || '';

      // Registry Source match
      let matchesRegistry = true;
      if (registrySource === 'federal') {
        matchesRegistry = solicitationId.includes('FED') || agency.toLowerCase().includes('federal') || agency.toLowerCase().includes('defense') || agency.toLowerCase().includes('nasa') || agency.toLowerCase().includes('sam');
      } else if (registrySource === 'state') {
        matchesRegistry = Boolean(item.state && item.state !== 'N/A') || agency.toLowerCase().includes('state') || agency.toLowerCase().includes('department') || agency.toLowerCase().includes('comptroller');
      } else if (registrySource === 'municipal') {
        matchesRegistry = agency.toLowerCase().includes('city') || agency.toLowerCase().includes('county') || agency.toLowerCase().includes('municipal') || agency.toLowerCase().includes('district');
      } else if (registrySource === 'watchlist') {
        matchesRegistry = isSaved(item.solicitationId);
      }

      // Search matches
      const matchesSearch = 
        solicitationId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agency.toLowerCase().includes(searchQuery.toLowerCase()) ||
        description.toLowerCase().includes(searchQuery.toLowerCase());

      // State match
      const matchesState = selectedStates.length === 0 || (item.state && selectedStates.includes(item.state));

      // Category match
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;

      // Status match
      const matchesStatus = selectedStatuses.length === 0 || (item.status && selectedStatuses.includes(item.status));

      // Budget match
      let matchesBudget = selectedBudgets.length === 0;
      if (!matchesBudget) {
        const estVal = item.pricing || 50000;
        matchesBudget = selectedBudgets.some(b => {
          if (b === 'Under 50k') return estVal < 50000;
          if (b === '50k-250k') return estVal >= 50000 && estVal <= 250000;
          if (b === 'Over 250k') return estVal > 250000;
          return false;
        });
      }

      return matchesRegistry && matchesSearch && matchesState && matchesCategory && matchesStatus && matchesBudget;
    });
  }, [opportunities, registrySource, searchQuery, selectedStates, selectedCategory, selectedStatuses, selectedBudgets, isSaved]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStates, selectedCategory, selectedStatuses, selectedBudgets]);

  // 4. Pagination slicing
  const paginatedOpportunities = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredOpportunities.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOpportunities, currentPage]);

  const totalPages = Math.ceil(filteredOpportunities.length / itemsPerPage);

  // 5. Database Analytics Calculation
  const stats = useMemo(() => {
    const totalCount = opportunities.length;
    const activeCount = opportunities.filter(o => o.status === 'open').length;
    const closedCount = opportunities.filter(o => o.status === 'closed').length;
    
    // Average budget (using actual and fallbacks)
    const totalVal = opportunities.reduce((acc, curr) => acc + (curr.pricing || 65000), 0);
    const avgVal = totalCount > 0 ? Math.round(totalVal / totalCount) : 0;
    
    // State coverage
    const uniqueStates = new Set(opportunities.map(o => o.state).filter(Boolean)).size;

    return {
      totalCount,
      activeCount,
      closedCount,
      avgVal,
      uniqueStates
    };
  }, [opportunities]);

  // Custom Chart: Top States & Distribution by Category
  const stateChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    opportunities.forEach(o => {
      if (o.state) counts[o.state] = (counts[o.state] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [opportunities]);

  const categoryChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    opportunities.forEach(o => {
      if (o.category) {
        // Group similar/smaller categories together
        let cat = o.category;
        if (cat.length > 25) cat = cat.substring(0, 25) + '...';
        counts[cat] = (counts[cat] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [opportunities]);

  // Clear all filters handler
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStates([]);
    setSelectedCategory('All');
    setSelectedStatuses([]);
    setSelectedBudgets([]);
  };

  // 6. Manual RFX Submit Handler
  const handleAddRFX = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formId || !formTitle || !formAgency) {
      alert('Please fill out required fields (RFX ID, Title, Agency).');
      return;
    }

    const docId = formId.toUpperCase().replace(/\s+/g, '-');
    const attachedDocuments = formFiles.length > 0
      ? formFiles.map((file, idx) => ({
          name: file.name,
          url: (file.dataUrl && file.dataUrl.length < 500 && !file.dataUrl.startsWith('data:') && file.dataUrl !== '#') 
            ? file.dataUrl 
            : `https://storage.procureflow.cloud/docs/${docId}/${encodeURIComponent(file.name)}`,
          version: 1.0,
          size: file.size,
          type: file.type
        }))
      : [
          { name: `${docId}-Requirements.pdf`, url: `https://storage.procureflow.cloud/docs/${docId}/Requirements.pdf`, version: 1.0 }
        ];

    const newOpportunity: RFXOpportunity = {
      solicitationId: docId,
      title: formTitle,
      agency: formAgency,
      state: formState.toUpperCase(),
      category: formCategory,
      pricing: parseFloat(formBudget) || 50000,
      dueDate: formDueDate ? new Date(formDueDate).toISOString() : new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString(),
      status: 'open',
      description: formDesc || 'Manually added custom bid opportunity matching corporate profile.',
      sourceUrl: formSourceUrl || 'https://manually-added-procure.gov',
      details: {
        generalinformation: {
          solicitationNumber: docId,
          estimatedBudget: `$${parseFloat(formBudget).toLocaleString()}`,
          buyingAgency: formAgency,
          status: 'Active/Open'
        },
        response: {
          rows: [
            { '0': '1', '1': 'Standard Service Delivery', '2': 'IT-001', '4': 'Service', '5': 'Custom Project Delivery Implementation', '10': '1', '11': 'Lot' }
          ]
        },
        solicitationdocuments: attachedDocuments
      }
    };

    try {
      const safePayload = ensureFirestoreSafeSize(newOpportunity);
      await setDoc(doc(db, 'solicitations', getSafeSolicitationDocId(docId)), safePayload);
      addToast({
        title: formTitle,
        message: `Successfully persisted custom RFX Opportunity (${attachedDocuments.length} document${attachedDocuments.length !== 1 ? 's' : ''} attached) into the procurement database.`,
        type: 'success'
      });
      // Reset form fields
      setFormId('');
      setFormTitle('');
      setFormAgency('');
      setFormDueDate('');
      setFormDesc('');
      setFormSourceUrl('');
      setFormFiles([]);
      setShowAddForm(false);
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, `solicitations/${getSafeSolicitationDocId(docId)}`);
    }
  };

  // 8. Interactive AI Fit Analysis inside Modal
  const handleAnalyzeFit = async () => {
    if (!selectedRFX) return;
    setAnalyzingFit(true);
    setFitResult(null);
    try {
      const vendorProfileMock = {
        companyName: "Quantum AI Solutions Inc.",
        capabilities: ["Information Technology", "Professional Services", "System Integration", "Cybersecurity"],
        naicsCodes: ["541511", "541512", "518210"]
      };

      const result = await geminiService.analyzeOpportunity(selectedRFX, vendorProfileMock);
      setFitResult(result);
      
      // Persist fit result to the bid document (pre-proposal state) so it persists!
      if (user) {
        const bidId = `${user.uid}_${selectedRFX.solicitationId}`;
        await setDoc(doc(db, 'bids', bidId), {
          solicitationId: selectedRFX.solicitationId,
          vendorUid: user.uid,
          status: 'analyzed',
          pricing: selectedRFX.pricing || 150000,
          analysis: result,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      addToast({
        title: 'Fit Assessment Complete',
        message: `Gemini calculated a ${result.score}% compatibility score with your vendor profile.`,
        type: 'success'
      });
    } catch (err: any) {
      console.error(err);
      addToast({
        title: 'Analysis Error',
        message: 'Could not execute Gemini fit scoring. Please try again.',
        type: 'danger'
      });
    } finally {
      setAnalyzingFit(false);
    }
  };

  // 9. Interactive RFX Response Proposal Draft Draftsman inside Modal
  const handleDraftResponse = async () => {
    if (!selectedRFX || !user) return;
    setGeneratingResponse(true);
    setResponseProgress('Reading RFX specifications...');
    
    try {
      const mockVendor = {
        companyName: "Quantum AI Solutions Inc.",
        ein: "12-3456789",
        address: "123 Quantum Way, Suite 100",
        contactEmail: "isa.hunter@quantumaiinc.solutions"
      };

      setTimeout(() => setResponseProgress('Synthesizing technical scope compliance requirements...'), 1200);
      setTimeout(() => setResponseProgress('Formulating pricing margin strategy...'), 2400);
      setTimeout(() => setResponseProgress('Assembling response document from corporate templates...'), 3600);

      const bidRes = await geminiService.generateBid(selectedRFX, mockVendor, []);
      
      const bidId = `${user.uid}_${selectedRFX.solicitationId}`;
      const bidData = {
        solicitationId: selectedRFX.solicitationId,
        vendorUid: user.uid,
        status: 'draft',
        pricing: selectedRFX.pricing || 150000,
        analysis: fitResult || { score: 85 },
        generatedContent: bidRes.proposal,
        pricingStrategy: bidRes.pricingStrategy || 'Standard fixed cost with 15% contingency reserve.',
        complianceChecklist: bidRes.complianceChecklist || ['Technical Scope Agreement', 'Vendor Certifications List'],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'bids', bidId), bidData);

      setTimeout(() => {
        addToast({
          title: 'RFX Proposal Generated',
          message: 'The proposal draft is ready and has been loaded into Response Documents.',
          type: 'success'
        });
        setGeneratingResponse(false);
        setResponseProgress('');
        setModalTab('response_documents'); // Focus immediately on Response Documents!
      }, 4800);

    } catch (err) {
      console.error(err);
      addToast({
        title: 'Drafting Failed',
        message: 'An error occurred while generating the draft. Please try again.',
        type: 'danger'
      });
      setGeneratingResponse(false);
      setResponseProgress('');
    }
  };

  // Helper: Count of days remaining formatted beautifully
  const getDaysRemaining = (dueDateStr: string) => {
    if (!dueDateStr) return { count: 0, text: 'N/A', isUrgent: false };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDateStr);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (days < 0) return { count: days, text: 'Passed', isUrgent: false };
    if (days === 0) return { count: days, text: 'Due Today', isUrgent: true };
    if (days === 1) return { count: days, text: 'Tomorrow', isUrgent: true };
    return { count: days, text: `${days} Days`, isUrgent: days <= 5 };
  };

  // Helper: Get color badge for State Code
  const getStateBadgeColor = (state: string) => {
    const map: Record<string, string> = {
      TX: 'bg-red-50 text-red-700 border-red-100',
      CA: 'bg-blue-50 text-blue-700 border-blue-100',
      NY: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      FL: 'bg-orange-50 text-orange-700 border-orange-100',
      GA: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    };
    return map[state] || 'bg-gray-50 text-gray-700 border-gray-100';
  };

  return (
    <div className="space-y-8 pb-10" id="rfx-database-root">
      {/* HEADER BAR */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-950 tracking-tight flex items-center gap-2">
            <Database className="w-6.5 h-6.5 text-indigo-600 animate-pulse" />
            RFX Procurement Database
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Browse corporate, state, and national RFX specifications. Ingest public feeds, inspect line items, and run AI fit scoring on-demand.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button 
            id="btn-browse-central-rfx"
            onClick={() => setActiveSubTab(activeSubTab === 'central-rfx' ? 'opportunities' : 'central-rfx')}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-lg shadow-2xs cursor-pointer transition-all"
          >
            <Database className="w-3.5 h-3.5 text-indigo-600" />
            {activeSubTab === 'central-rfx' ? 'View My RFX Database' : 'Browse Central State Feeds'}
          </button>
          <button 
            id="btn-add-rfx"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-2xs cursor-pointer transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Custom RFX
          </button>
        </div>
      </div>

      {/* Sub-Tabs Navigation */}
      <div className="flex border-b border-gray-200 gap-6 overflow-x-auto scrollbar-thin">
        <button
          onClick={() => setActiveSubTab('opportunities')}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
            activeSubTab === 'opportunities'
              ? 'border-indigo-600 text-indigo-600 font-bold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span>My RFX Database</span>
          <span className="px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded-full font-mono">
            {opportunities.length}
          </span>
        </button>

        <button
          onClick={() => setActiveSubTab('central-rfx')}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap ${
            activeSubTab === 'central-rfx'
              ? 'border-indigo-600 text-indigo-600 font-bold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Database className="w-3.5 h-3.5 text-indigo-600" />
          <span>Central State Solicitations</span>
          <span className="px-1.5 py-0.2 text-[10px] bg-slate-100 text-slate-700 rounded font-semibold uppercase tracking-wider">
            Read-Only
          </span>
        </button>

        <button
          onClick={() => setActiveSubTab('contracts')}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeSubTab === 'contracts'
              ? 'border-indigo-600 text-indigo-600 font-bold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          State-Level Contracts
        </button>

        <button
          onClick={() => setActiveSubTab('historical-rfx')}
          className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            activeSubTab === 'historical-rfx'
              ? 'border-indigo-600 text-indigo-600 font-bold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Historical RFXs
        </button>

        {user?.email && (user.email.endsWith('@quantumaiinc.tech') || user.email.endsWith('@quantumaiinc.solutions')) && (
          <button
            onClick={() => setActiveSubTab('sgpvp')}
            className={`pb-3 text-sm font-semibold border-b-2 transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              activeSubTab === 'sgpvp'
                ? 'border-indigo-600 text-indigo-600 font-bold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
            Internal Harvester
          </button>
        )}
      </div>

      {activeSubTab === 'central-rfx' ? (
        <CentralStateSolicitationsSection
          userParticipatedIds={userParticipatedIds}
          onOpenInUserWorkspace={(solicitationId) => {
            setActiveSubTab('opportunities');
            const target = opportunities.find(o => o.solicitationId === solicitationId);
            if (target) {
              setSelectedRFX(target);
            }
          }}
          onItemCopied={(copiedItem) => {
            addToast({
              title: 'Added to My RFX Database',
              message: `${copiedItem.solicitationId} unlocked! Full line items, documents, and Proposal Studio are now available.`,
              type: 'success'
            });
            setUserParticipatedIds(prev => new Set([...prev, copiedItem.solicitationId]));
          }}
        />
      ) : activeSubTab === 'sgpvp' ? (
        <SgpvpRetrievalDashboard />
      ) : activeSubTab === 'contracts' ? (
        <StateContractsDatabaseSection />
      ) : activeSubTab === 'historical-rfx' ? (
        <HistoricalBidsAnalysisSection />
      ) : (
        <>
          {/* STATS & METRICS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" id="rfx-metrics-dashboard">
        <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Database Size</p>
              <h3 className="text-2xl font-black text-gray-900 mt-1">{loading ? '...' : stats.totalCount}</h3>
            </div>
            <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
              <Database className="w-5 h-5" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-3 flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
            Real-time synced Firestore collection
          </p>
        </div>

        <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Active Open RFXs</p>
              <h3 className="text-2xl font-black text-emerald-600 mt-1">{loading ? '...' : stats.activeCount}</h3>
            </div>
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
              <Activity className="w-5 h-5 animate-pulse" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-3">
            Available for immediate proposal response
          </p>
        </div>

        <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Avg Est. Opportunity</p>
              <h3 className="text-2xl font-black text-gray-900 mt-1">${loading ? '...' : stats.avgVal.toLocaleString()}</h3>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg text-amber-600">
              <DollarSign className="w-5 h-5" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-3">
            Estimated gross procurement project value
          </p>
        </div>

        <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs relative overflow-hidden flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Jurisdiction Coverage</p>
              <h3 className="text-2xl font-black text-gray-900 mt-1">{loading ? '...' : `${stats.uniqueStates} States`}</h3>
            </div>
            <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
              <MapPin className="w-5 h-5" />
            </div>
          </div>
          <p className="text-[10px] text-gray-500 mt-3">
            Represented states in crawled feed list
          </p>
        </div>
      </div>

      {/* INTERACTIVE DATA VISUALIZATIONS CHARTS */}
      {!loading && opportunities.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" id="rfx-charts-dashboard">
          {/* STATE COVERAGE BAR CHART */}
          <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs">
            <h4 className="text-xs font-bold text-gray-800 mb-4 tracking-tight flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              Opportunities Volume by Top States
            </h4>
            <div className="space-y-3.5">
              {stateChartData.map((item, index) => {
                const percentage = Math.round((item.value / opportunities.length) * 100);
                return (
                  <div key={item.name} className="space-y-1">
                    <div className="flex justify-between text-[11px] font-medium text-gray-600">
                      <span className="flex items-center gap-1.5">
                        <span className={`w-2.5 h-2.5 rounded-full ${index === 0 ? 'bg-indigo-600' : index === 1 ? 'bg-indigo-400' : 'bg-indigo-200'}`} />
                        {item.name} State Portal
                      </span>
                      <span>{item.value} RFXs ({percentage}%)</span>
                    </div>
                    <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }} 
                        animate={{ width: `${percentage}%` }} 
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        className={`h-full rounded-full ${index === 0 ? 'bg-indigo-600' : index === 1 ? 'bg-indigo-500' : 'bg-indigo-300'}`} 
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CATEGORY DONUT GRAPH & LEGEND */}
          <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs">
            <h4 className="text-xs font-bold text-gray-800 mb-4 tracking-tight flex items-center gap-1.5">
              <Activity className="w-4 h-4 text-emerald-500" />
              Commodity Category Distribution
            </h4>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* SVG Circular Donut Chart */}
              <div className="relative w-36 h-36 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="38" stroke="#f3f4f6" strokeWidth="10" fill="transparent" />
                  {/* Generate circular rings based on top segments */}
                  {categoryChartData.map((item, idx) => {
                    const beforeSum = categoryChartData.slice(0, idx).reduce((acc, c) => acc + c.value, 0);
                    const totalSum = opportunities.length;
                    const strokeDasharray = `${(item.value / totalSum) * 238.76} 238.76`;
                    const strokeDashoffset = `-${(beforeSum / totalSum) * 238.76}`;
                    const colors = ['#4f46e5', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6'];
                    return (
                      <circle 
                        key={item.name}
                        cx="50" 
                        cy="50" 
                        r="38" 
                        stroke={colors[idx] || '#cbd5e1'} 
                        strokeWidth="10" 
                        strokeDasharray={strokeDasharray}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        fill="transparent" 
                      />
                    );
                  })}
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-[9px] font-bold text-gray-400 tracking-widest uppercase">TOP SEGMENT</span>
                  <span className="text-xs font-black text-gray-800 mt-0.5">
                    {categoryChartData[0] ? `${Math.round((categoryChartData[0].value / opportunities.length) * 100)}%` : '0%'}
                  </span>
                </div>
              </div>

              {/* Legends list */}
              <div className="flex-1 w-full space-y-2">
                {categoryChartData.map((item, idx) => {
                  const colors = ['bg-indigo-600', 'bg-emerald-500', 'bg-amber-500', 'bg-blue-500', 'bg-purple-500'];
                  return (
                    <div key={item.name} className="flex items-center justify-between text-[11px] text-gray-600">
                      <span className="flex items-center gap-1.5 truncate max-w-[180px]">
                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${colors[idx] || 'bg-slate-300'}`} />
                        <span className="truncate">{item.name}</span>
                      </span>
                      <span className="font-semibold">{item.value} RFXs</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REGISTRY SOURCE NESTED TABS */}
      <div className="flex border-b border-gray-200 gap-4 mb-4 overflow-x-auto shrink-0">
        <button
          type="button"
          onClick={() => setRegistrySource('all')}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            registrySource === 'all' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All Active Registries ({opportunities.length})
        </button>
        <button
          type="button"
          onClick={() => setRegistrySource('federal')}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            registrySource === 'federal' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Federal Sourcing (SAM.gov)
        </button>
        <button
          type="button"
          onClick={() => setRegistrySource('state')}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            registrySource === 'state' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          State Portals & NASPO
        </button>
        <button
          type="button"
          onClick={() => setRegistrySource('municipal')}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            registrySource === 'municipal' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Municipal & Local Sourcing
        </button>
        <button
          type="button"
          onClick={() => setRegistrySource('watchlist')}
          className={`pb-2.5 text-xs font-bold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
            registrySource === 'watchlist' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Saved Watchlist
        </button>
      </div>

      {/* FILTER CONTROLS & VIEWER BAR */}
      <div className="p-5 bg-white border border-gray-100 rounded-xl shadow-xs space-y-4">
        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-3">
          {/* SEARCH BOX */}
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              id="search-rfx-db"
              type="text"
              placeholder="Search RFX title, Project ID, buying agency, key details..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none transition-all placeholder:text-gray-400"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 bg-gray-200/50 rounded-full"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* STATE FILTER SELECT */}
            <div className="w-44">
              <MultiSelectCombo
                options={mappedStateOptions}
                selectedValues={selectedStates}
                onChange={setSelectedStates}
                placeholder="All States"
                icon={<MapPin className="w-3.5 h-3.5" />}
              />
            </div>

            {/* BUDGET RANGE SELECT */}
            <div className="w-44">
              <MultiSelectCombo
                options={budgetOptions}
                selectedValues={selectedBudgets}
                onChange={setSelectedBudgets}
                placeholder="All Budgets"
                icon={<DollarSign className="w-3.5 h-3.5" />}
              />
            </div>

            {/* STATUS FILTER */}
            <div className="w-44">
              <MultiSelectCombo
                options={statusOptions}
                selectedValues={selectedStatuses}
                onChange={setSelectedStatuses}
                placeholder="All Statuses"
                icon={<Activity className="w-3.5 h-3.5" />}
              />
            </div>
          </div>
        </div>

        {/* ACTIVE FILTER PILLS */}
        {(selectedStates.length > 0 || selectedCategory !== 'All' || selectedStatuses.length > 0 || selectedBudgets.length > 0 || searchQuery) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100">
            <span className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mr-1">Active Filters:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-indigo-100">
                Keyword: "{searchQuery}"
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchQuery('')} />
              </span>
            )}
            {selectedStates.length > 0 && (
              <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-red-100">
                States: {selectedStates.join(', ')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedStates([])} />
              </span>
            )}
            {selectedCategory !== 'All' && (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-emerald-100">
                Category: {selectedCategory}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCategory('All')} />
              </span>
            )}
            {selectedStatuses.length > 0 && (
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-amber-100">
                Statuses: {selectedStatuses.join(', ')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedStatuses([])} />
              </span>
            )}
            {selectedBudgets.length > 0 && (
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border border-blue-100">
                Budgets: {selectedBudgets.join(', ')}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedBudgets([])} />
              </span>
            )}
            <button 
              onClick={clearFilters}
              className="text-[11px] text-gray-500 hover:text-gray-700 font-bold ml-auto cursor-pointer flex items-center gap-1 underline decoration-dotted"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* CORE RFX CONTENT LIST */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 space-y-4">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-gray-500 font-medium">Reading RFX specifications from Firestore...</p>
        </div>
      ) : filteredOpportunities.length === 0 ? (
        <div className="p-16 text-center bg-white border border-gray-100 rounded-xl shadow-xs space-y-4">
          <div className="w-14 h-14 bg-gray-50 text-gray-300 rounded-full flex items-center justify-center mx-auto">
            <Database className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">No Match in Procurement Feed</h3>
            <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
              No opportunities matching your filtered preferences were found. Try expanding search tags, clearing active criteria, or crawl real-time state feeds.
            </p>
          </div>
          <button 
            onClick={clearFilters}
            className="px-4 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg cursor-pointer transition-all"
          >
            Reset Database Explorer
          </button>
        </div>
      ) : (
        <div className="space-y-6">
            {/* TABLE VIEWER SHEET LAYOUT */}
            <div className="bg-white border border-gray-100 rounded-xl shadow-2xs overflow-hidden" id="rfx-table-wrapper">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      <th className="px-6 py-3.5">RFX ID</th>
                      <th className="px-6 py-3.5">State</th>
                      <th className="px-6 py-3.5">Title & Issuing Agency</th>
                      <th className="px-6 py-3.5">Est. Budget</th>
                      <th className="px-6 py-3.5">Due Date</th>
                      <th className="px-6 py-3.5">Status</th>
                      <th className="px-6 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 text-xs">
                    {paginatedOpportunities.map((rfx) => {
                      const daysLeft = getDaysRemaining(rfx.dueDate);
                      const bookmarked = isSaved(rfx.solicitationId);
                      return (
                        <tr key={rfx.solicitationId} className="hover:bg-gray-50/50 transition-all">
                          <td className="px-6 py-4 font-mono font-semibold text-gray-600">{rfx.solicitationId}</td>
                          <td className="px-6 py-4">
                            <span className={`text-[9px] font-black tracking-wider uppercase px-2 py-0.5 rounded-md border ${getStateBadgeColor(rfx.state)}`}>
                              {rfx.state}
                            </span>
                          </td>
                          <td className="px-6 py-4 max-w-[320px]">
                            <p className="font-bold text-gray-950 truncate" title={rfx.title}>{rfx.title}</p>
                            <p className="text-[10px] text-gray-500 truncate mt-0.5">{rfx.agency}</p>
                          </td>
                          <td className="px-6 py-4 font-semibold text-gray-900">${(rfx.pricing || 50000).toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 font-semibold ${daysLeft.isUrgent ? 'text-red-600 bg-red-50 px-2 py-0.5 rounded-md' : 'text-gray-700'}`}>
                              <Clock className="w-3 h-3" />
                              {daysLeft.text}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-md ${rfx.status === 'open' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-gray-50 text-gray-600'}`}>
                              {rfx.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button 
                                onClick={() => toggleSave(rfx)}
                                className={`p-1.5 border rounded-lg cursor-pointer ${bookmarked ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600'}`}
                                title={bookmarked ? "Unwatch" : "Watch"}
                              >
                                {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                              </button>
                              <button 
                                onClick={() => { setSelectedRFX(rfx); setFitResult(null); }}
                                className="px-3 py-1.5 text-[11px] font-bold text-gray-700 bg-white border border-gray-200 hover:border-indigo-200 hover:text-indigo-600 rounded-lg cursor-pointer transition-all"
                              >
                                Inspect
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          {/* PAGINATION CONTROLLER PANEL */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center bg-white p-4 border border-gray-100 rounded-xl shadow-2xs">
              <span className="text-xs text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-800">{((currentPage - 1) * itemsPerPage) + 1}</span> to <span className="font-bold text-gray-800">{Math.min(currentPage * itemsPerPage, filteredOpportunities.length)}</span> of <span className="font-bold text-indigo-600">{filteredOpportunities.length}</span> procurement records
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  className="p-1.5 border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-7 h-7 text-xs font-bold rounded-lg cursor-pointer transition-all ${currentPage === page ? 'bg-indigo-600 text-white shadow-xs' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  className="p-1.5 border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* --- MODAL 1: CRAWLER / CRAWL PORTALS SCREEN --- */}
      <AnimatePresence>

      </AnimatePresence>

      {/* --- MODAL 2: ADD CUSTOM RFX DRAWER FORM --- */}
      <AnimatePresence>
        {showAddForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-end bg-gray-950/30 backdrop-blur-xs">
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-md h-full bg-white shadow-2xl border-l border-gray-100 flex flex-col justify-between"
            >
              {/* Form Title */}
              <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-bold text-gray-950 text-sm">Add Custom RFX Opportunity</h3>
                </div>
                <button 
                  onClick={() => setShowAddForm(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 bg-gray-50 rounded-lg cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Form Fields body */}
              <form onSubmit={handleAddRFX} className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-400 uppercase">RFX / Solicitation ID <span className="text-red-500">*</span></label>
                  <input 
                    type="text"
                    required
                    placeholder="e.g. TX-2026-802"
                    value={formId}
                    onChange={(e) => setFormId(e.target.value)}
                    className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-400 uppercase">Opportunity Title <span className="text-red-500">*</span></label>
                  <input 
                    type="text"
                    required
                    placeholder="e.g. Broadband Expansion and Infrastructure Deployment"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-400 uppercase">Buying Agency / Department <span className="text-red-500">*</span></label>
                  <input 
                    type="text"
                    required
                    placeholder="e.g. Texas Department of Agriculture"
                    value={formAgency}
                    onChange={(e) => setFormAgency(e.target.value)}
                    className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400 uppercase">State Jurisdiction</label>
                    <select 
                      value={formState}
                      onChange={(e) => setFormState(e.target.value)}
                      className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg outline-none cursor-pointer focus:bg-white focus:border-indigo-500"
                    >
                      {US_STATES.map((st) => (
                        <option key={st.code} value={st.code}>
                          {st.code} - {st.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400 uppercase">Commodity Category</label>
                    <select 
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                      className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg outline-none cursor-pointer"
                    >
                      <option value="Information Technology">Information Technology</option>
                      <option value="Professional Services">Professional Services</option>
                      <option value="Facilities Maintenance">Facilities Maintenance</option>
                      <option value="Cybersecurity Services">Cybersecurity Services</option>
                      <option value="Hardware Procurement">Hardware Procurement</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400 uppercase">Estimated Budget ($)</label>
                    <input 
                      type="number"
                      placeholder="150000"
                      value={formBudget}
                      onChange={(e) => setFormBudget(e.target.value)}
                      className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[11px] font-bold text-gray-400 uppercase">Response Due Date</label>
                    <input 
                      type="date"
                      value={formDueDate}
                      onChange={(e) => setFormDueDate(e.target.value)}
                      className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 rounded-lg outline-none cursor-pointer"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-400 uppercase">Original Feed Reference URL</label>
                  <input 
                    type="url"
                    placeholder="https://stateprocurement.gov/opportunity"
                    value={formSourceUrl}
                    onChange={(e) => setFormSourceUrl(e.target.value)}
                    className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-400 uppercase">Scope Statement / Description</label>
                  <textarea 
                    rows={3}
                    placeholder="Outline high-level requirements and specifications of this RFX opportunity..."
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    className="w-full p-2.5 text-xs text-gray-800 bg-gray-50 border border-gray-200 focus:bg-white focus:border-indigo-500 rounded-lg outline-none resize-none"
                  />
                </div>

                {/* Multiple File Upload Section */}
                <div className="space-y-2 pt-1 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-bold text-gray-500 uppercase flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5 text-indigo-600" />
                      Solicitation Documents & Attachments ({formFiles.length})
                    </label>
                    {formFiles.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setFormFiles([])}
                        className="text-[10px] text-rose-500 hover:text-rose-700 font-semibold cursor-pointer"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  {/* Dropzone / Upload Box */}
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-indigo-400 bg-slate-50/70 hover:bg-indigo-50/30 rounded-xl p-4 cursor-pointer transition-all text-center group">
                    <input 
                      type="file"
                      multiple
                      onChange={handleFormFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json,image/*"
                    />
                    <div className="p-2 bg-white rounded-lg border border-gray-200 text-indigo-600 shadow-2xs group-hover:scale-105 transition-transform mb-1.5">
                      <Upload className="w-4 h-4" />
                    </div>
                    <span className="text-xs font-bold text-gray-700 group-hover:text-indigo-700">
                      Upload Multiple Documents
                    </span>
                    <span className="text-[10px] text-gray-400 mt-0.5">
                      PDF, DOCX, Excel / CSV, TXT, or Specs (Select or Drag & Drop)
                    </span>
                  </label>

                  {/* Attached Files List */}
                  {formFiles.length > 0 && (
                    <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                      {formFiles.map((file, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-2 text-xs shadow-2xs"
                        >
                          <div className="flex items-center gap-2 min-w-0 pr-2">
                            <File className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                            <span className="font-semibold text-gray-800 truncate text-[11px]">
                              {file.name}
                            </span>
                            <span className="text-[9.5px] text-gray-400 shrink-0 font-mono">
                              {(file.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFormFile(idx)}
                            className="p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded transition-colors cursor-pointer shrink-0"
                            title="Remove file"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>

              {/* Form Footer action buttons */}
              <div className="p-4 border-t border-gray-100 bg-gray-50 flex gap-2">
                <button 
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 py-2 text-xs font-bold text-gray-500 hover:text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg cursor-pointer transition-all"
                >
                  Discard
                </button>
                <button 
                  onClick={handleAddRFX}
                  className="flex-1 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg cursor-pointer transition-all shadow-2xs"
                >
                  Save Opportunity
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- MODAL 3: RICH SPECIFICATION DETAIL & AI WORKBENCH --- */}
      <AnimatePresence>
        {selectedRFX && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/30 backdrop-blur-xs overflow-y-auto">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden my-8"
            >
              {/* Header */}
              <div className="px-6 py-4.5 border-b border-gray-100 bg-gray-50/50 flex justify-between items-start gap-4">
                <div>
                  <span className="text-[9px] font-mono tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100 uppercase">
                    {selectedRFX.solicitationId}
                  </span>
                  <h3 className="font-black text-gray-950 text-base mt-1.5 leading-snug">{selectedRFX.title}</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                    <Building className="w-3.5 h-3.5 text-gray-400" />
                    {selectedRFX.agency}
                  </p>
                </div>
                <button 
                  onClick={() => { setSelectedRFX(null); setFitResult(null); }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 bg-white border border-gray-200 rounded-lg cursor-pointer shadow-3xs shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Tab Selector Buttons */}
              <div className="flex border-b border-gray-100 bg-slate-50 px-6 shrink-0 overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setModalTab('details')}
                  className={`py-3 px-5 text-xs font-bold border-b-2 uppercase tracking-wide transition-all -mb-px shrink-0 flex items-center gap-1.5 ${
                    modalTab === 'details' ? "border-indigo-600 text-indigo-700 font-extrabold" : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Sourcing Specifications
                </button>
                <button
                  type="button"
                  onClick={() => setModalTab('response_documents')}
                  className={`py-3 px-5 text-xs font-bold border-b-2 uppercase tracking-wide transition-all -mb-px shrink-0 flex items-center gap-2 ${
                    modalTab === 'response_documents' ? "border-indigo-600 text-indigo-700 font-extrabold" : "border-transparent text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  Response Documents {currentBid?.generatedContent && (
                    <span className="bg-emerald-100 text-emerald-800 text-[9px] px-1.5 py-0.5 rounded-full font-black">Ready</span>
                  )}
                </button>
              </div>

              {modalTab === 'details' && (
                <>
                  {/* Main Body Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 h-[480px]">
                    {/* Left Side: Specifications Details */}
                    <div className="lg:col-span-3 p-6 overflow-y-auto space-y-5 scrollbar-thin">
                      <div className="space-y-1">
                        <h4 className="text-[11px] font-bold text-gray-400 tracking-wider uppercase">Project Description / Commodity</h4>
                        <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">
                          {selectedRFX.description}
                        </p>
                      </div>

                      {/* General attributes metadata */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <h5 className="text-[10px] font-bold text-gray-400 uppercase">Jurisdiction Portal</h5>
                          <p className="text-xs text-gray-800 font-bold flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                            State of {selectedRFX.state}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <h5 className="text-[10px] font-bold text-gray-400 uppercase">Response Due Date</h5>
                          <p className="text-xs text-red-600 font-black flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            {new Date(selectedRFX.dueDate).toLocaleDateString()} ({getDaysRemaining(selectedRFX.dueDate).text})
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <h5 className="text-[10px] font-bold text-gray-400 uppercase">Contract Budget Estimate</h5>
                          <p className="text-xs text-emerald-700 font-extrabold flex items-center gap-1">
                            <DollarSign className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            ${(selectedRFX.pricing || 150000).toLocaleString()} USD
                          </p>
                        </div>
                        <div className="space-y-1">
                          <h5 className="text-[10px] font-bold text-gray-400 uppercase">Original Sourcing URL</h5>
                          {selectedRFX.sourceUrl ? (
                            <a 
                              href={selectedRFX.sourceUrl} 
                              target="_blank" 
                              referrerPolicy="no-referrer"
                              className="text-xs text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1"
                            >
                              <ExternalLink className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                              View State Source
                            </a>
                          ) : (
                            <p className="text-xs text-gray-400">N/A</p>
                          )}
                        </div>
                      </div>

                      {/* SPECIFICATION ITEMS ROWS TABLE (REPLACED WITH HIGH FIDELITY SPREADSHEET) */}
                      {spreadsheetRows && spreadsheetRows.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <h4 className="text-[11px] font-black text-gray-400 tracking-wider uppercase flex items-center gap-1.5">
                              <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                              Interactive Bid & Cost Worksheet (.xlsx)
                            </h4>
                            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md font-bold border border-emerald-100 animate-pulse">
                              ● Live Connected Sheet
                            </span>
                          </div>

                          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-left font-sans flex flex-col">
                            {/* Google Sheets Title & Ribbon */}
                            <div className="bg-slate-50 border-b border-slate-200 p-2 flex flex-col gap-1 px-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <div className="p-1.5 bg-emerald-600 text-white rounded-md">
                                    <FileSpreadsheet className="w-3.5 h-3.5" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs font-black text-slate-800 font-mono">
                                        {selectedRFX?.solicitationId || 'RFP'}_Bid_Costing.xlsx
                                      </span>
                                      <span className="bg-slate-200 text-slate-600 font-mono text-[8px] px-1 py-0.2 rounded font-semibold">Live Editable</span>
                                    </div>
                                    <div className="flex gap-2.5 text-[9.5px] text-slate-400 font-medium">
                                      <span className="hover:text-slate-600 cursor-pointer">File</span>
                                      <span className="hover:text-slate-600 cursor-pointer">Edit</span>
                                      <span className="hover:text-slate-600 cursor-pointer">View</span>
                                      <span className="hover:text-slate-600 cursor-pointer">Format</span>
                                      <span className="hover:text-slate-600 cursor-pointer font-bold text-emerald-700">Data</span>
                                      <span className="hover:text-slate-600 cursor-pointer">Tools</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Excel Toolbar */}
                            <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between gap-2 overflow-x-auto select-none">
                              <div className="flex items-center gap-1 shrink-0">
                                <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500" title="Undo"><Undo className="w-3 h-3" /></button>
                                <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500" title="Redo"><Redo className="w-3 h-3" /></button>
                                <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500" title="Print"><Printer className="w-3 h-3" /></button>
                                <div className="h-4 w-px bg-slate-200 mx-1" />
                                <span className="text-[10px] text-slate-500 font-mono font-medium bg-white px-1.5 py-0.5 border border-slate-200 rounded">Arial</span>
                                <div className="h-4 w-px bg-slate-200 mx-1" />
                                <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-700 font-black text-[10px] font-mono" title="Bold">B</button>
                                <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-700 italic text-[10px] font-mono" title="Italic">I</button>
                                <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500 bg-emerald-100" title="Borders"><LayoutTemplate className="w-3 h-3 text-emerald-700" /></button>
                              </div>

                              {/* Active Cell & Formula Bar */}
                              <div className="flex-1 flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-0.5">
                                <div className="text-[10px] text-emerald-700 font-mono font-bold border-r border-slate-200 pr-2 select-none">
                                  {selectedCell ? `${selectedCell.field === 'price' ? 'E' : selectedCell.field === 'qty' ? 'D' : 'B'}${selectedCell.row + 2}` : 'A1'}
                                </div>
                                <span className="text-[10px] text-slate-400 italic font-mono select-none">fx</span>
                                <input
                                  type="text"
                                  value={
                                    selectedCell 
                                      ? selectedCell.field === 'price' 
                                        ? spreadsheetRows[selectedCell.row]?.price || ''
                                        : selectedCell.field === 'qty'
                                          ? spreadsheetRows[selectedCell.row]?.qty || ''
                                          : spreadsheetRows[selectedCell.row]?.item || ''
                                      : ''
                                  }
                                  onChange={(e) => {
                                    if (!selectedCell) return;
                                    const val = e.target.value;
                                    const updated = [...spreadsheetRows];
                                    if (selectedCell.field === 'price') {
                                      const p = parseFloat(val) || 0;
                                      updated[selectedCell.row].price = p;
                                      updated[selectedCell.row].total = updated[selectedCell.row].qty * p;
                                    } else if (selectedCell.field === 'qty') {
                                      const q = parseInt(val) || 0;
                                      updated[selectedCell.row].qty = q;
                                      updated[selectedCell.row].total = q * updated[selectedCell.row].price;
                                    } else {
                                      updated[selectedCell.row].item = val;
                                    }
                                    setSpreadsheetRows(updated);
                                  }}
                                  className="flex-1 bg-transparent border-none outline-none text-[10.5px] font-mono text-slate-800 p-0 focus:ring-0 focus:outline-none"
                                  placeholder="Cell Value"
                                />
                              </div>
                            </div>

                            {/* Grid Container */}
                            <div className="overflow-x-auto scrollbar-thin max-h-[220px]">
                              <table className="w-full border-collapse text-[10.5px] font-mono text-slate-700">
                                {/* Grid Letter Headers */}
                                <thead className="sticky top-0 bg-slate-50 z-10">
                                  <tr className="bg-slate-100/80 divide-x divide-slate-200 border-b border-slate-200 text-center select-none text-[9.5px] text-slate-500 font-medium">
                                    <th className="w-8 bg-slate-200/50" />
                                    <th className="px-3 py-1 text-left w-14">A (Line)</th>
                                    <th className="px-3 py-1 text-left min-w-[200px]">B (Commodity Item Description)</th>
                                    <th className="px-2 py-1">C (Unit)</th>
                                    <th className="px-3 py-1 text-right w-14">D (Qty)</th>
                                    <th className="px-3 py-1 text-right w-24">E (Unit Price)</th>
                                    <th className="px-3 py-1 text-right bg-emerald-50/50 text-emerald-900 font-bold w-28 border-l border-slate-200">F (Total Cost)</th>
                                  </tr>
                                </thead>
                                {/* Grid Body */}
                                <tbody className="divide-y divide-slate-150">
                                  {spreadsheetRows.map((row, rIdx) => (
                                    <tr key={rIdx} className="hover:bg-slate-50/50 group/row divide-x divide-slate-150">
                                      {/* Row Number Gutter */}
                                      <td className="w-8 bg-slate-100/80 text-center text-[9px] text-slate-400 font-medium select-none border-r border-slate-200">
                                        {rIdx + 2}
                                      </td>
                                      
                                      {/* Col A: Line Code */}
                                      <td className="px-3 py-1.5 text-slate-400 font-bold select-none text-[10px]">
                                        {row.line}
                                      </td>

                                      {/* Col B: Item Description */}
                                      <td 
                                        onClick={() => setSelectedCell({ row: rIdx, col: 1, field: 'item' })}
                                        className={`px-3 py-1.5 truncate max-w-[200px] cursor-cell transition-all font-sans text-xs ${
                                          selectedCell?.row === rIdx && selectedCell?.field === 'item'
                                            ? 'outline-2 outline-emerald-500 bg-emerald-50/20 font-semibold text-slate-900'
                                            : 'text-slate-800'
                                        }`}
                                      >
                                        {row.item}
                                      </td>

                                      {/* Col C: Unit */}
                                      <td className="px-2 py-1.5 text-center text-slate-500 text-[10px] select-none">
                                        {row.unit}
                                      </td>

                                      {/* Col D: Qty */}
                                      <td 
                                        onClick={() => setSelectedCell({ row: rIdx, col: 3, field: 'qty' })}
                                        className={`px-3 py-1.5 text-right cursor-cell transition-all ${
                                          selectedCell?.row === rIdx && selectedCell?.field === 'qty'
                                            ? 'outline-2 outline-emerald-500 bg-emerald-50/20 font-black text-slate-900'
                                            : 'text-slate-600 font-medium'
                                        }`}
                                      >
                                        {row.qty}
                                      </td>

                                      {/* Col E: Unit Price (Editable) */}
                                      <td 
                                        onClick={() => setSelectedCell({ row: rIdx, col: 4, field: 'price' })}
                                        className={`px-3 py-1.5 text-right cursor-cell transition-all relative ${
                                          selectedCell?.row === rIdx && selectedCell?.field === 'price'
                                            ? 'outline-2 outline-emerald-500 bg-emerald-50/25 font-black text-slate-900'
                                            : 'text-slate-900 hover:bg-slate-100/50 font-bold'
                                        }`}
                                      >
                                        <div className="flex items-center justify-end">
                                          <span className="text-[9px] text-slate-400 mr-0.5">$</span>
                                          <input
                                            type="number"
                                            value={row.price}
                                            onChange={(e) => {
                                              const updated = [...spreadsheetRows];
                                              const p = parseFloat(e.target.value) || 0;
                                              updated[rIdx].price = p;
                                              updated[rIdx].total = updated[rIdx].qty * p;
                                              setSpreadsheetRows(updated);
                                            }}
                                            className="w-14 bg-transparent text-right outline-none border-none p-0 focus:ring-0 font-bold"
                                          />
                                        </div>
                                      </td>

                                      {/* Col F: Total Cost */}
                                      <td className="px-3 py-1.5 text-right font-bold text-slate-900 bg-emerald-50/20">
                                        ${row.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                  ))}

                                  {/* Grand Total Row */}
                                  <tr className="bg-emerald-50/40 divide-x divide-slate-150 font-bold border-t-2 border-double border-slate-300">
                                    <td className="w-8 bg-slate-200/50 text-center text-[9px] text-slate-500 select-none">
                                      {spreadsheetRows.length + 2}
                                    </td>
                                    <td className="px-3 py-2 text-slate-400 text-[10px]">TOTAL</td>
                                    <td className="px-3 py-2 text-emerald-950 font-sans text-xs">
                                      Compiled SOW Pricing (Formula: SUM(F2:F{spreadsheetRows.length + 1}))
                                    </td>
                                    <td className="px-2 py-2" />
                                    <td className="px-3 py-2 text-right">
                                      {spreadsheetRows.reduce((acc, r) => acc + r.qty, 0)}
                                    </td>
                                    <td className="px-3 py-2" />
                                    <td className="px-3 py-2 text-right text-emerald-800 font-extrabold text-[11px] underline decoration-double">
                                      ${spreadsheetRows.reduce((acc, r) => acc + r.total, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {/* Sheets Tabs Footer */}
                            <div className="bg-slate-100 border-t border-slate-200 px-4 py-1 flex items-center gap-1.5 text-[9.5px] font-bold select-none text-slate-500">
                              <span className="text-slate-400 mr-1 shrink-0">➕ ☰</span>
                              <div className="bg-white border-x border-t border-slate-200 rounded-t px-3 py-1 text-emerald-700 flex items-center gap-1.5 -mb-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Cost Schedule (Core Pricing)
                              </div>
                              <div className="hover:bg-slate-200 px-3 py-1 rounded-t cursor-pointer">
                                SLA Labor Rates
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* DOCUMENTS SECTION */}
                      {selectedRFX.details?.solicitationdocuments && selectedRFX.details.solicitationdocuments.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-[11px] font-bold text-gray-400 tracking-wider uppercase flex items-center justify-between">
                            <span>Sourcing Documents & Attachments</span>
                            <span className="text-[10px] text-gray-500 font-mono font-normal">({selectedRFX.details.solicitationdocuments.length} files)</span>
                          </h4>
                          <div className="space-y-1.5">
                            {selectedRFX.details.solicitationdocuments.map((docItem, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-gray-50 hover:bg-indigo-50/40 px-3 py-2 rounded-lg border border-gray-100 transition-colors text-xs">
                                <div className="flex items-center gap-2 min-w-0 pr-2">
                                  <FileText className="w-4 h-4 text-indigo-500 shrink-0" />
                                  <span className="font-semibold text-gray-700 truncate">
                                    {docItem.name || 'document.pdf'}
                                  </span>
                                  {docItem.size && (
                                    <span className="text-[10px] text-gray-400 font-mono shrink-0">
                                      {(docItem.size / 1024).toFixed(1)} KB
                                    </span>
                                  )}
                                </div>
                                {docItem.url && docItem.url !== '#' ? (
                                  <a 
                                    href={docItem.url} 
                                    download={docItem.name} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2 py-0.5 rounded-md font-bold shrink-0 transition-colors"
                                  >
                                    Download / View
                                  </a>
                                ) : (
                                  <span className="text-[10px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md font-bold shrink-0">
                                    Ready
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Side: AI Intelligence Workbench */}
                    <div className="lg:col-span-2 p-6 overflow-y-auto space-y-6 bg-slate-50/50 scrollbar-thin">
                      <div>
                        <h4 className="text-xs font-black text-gray-900 tracking-tight flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-indigo-600" />
                          RFX AI Intelligence Center
                        </h4>
                        <p className="text-[10.5px] text-gray-500 mt-1">
                          Analyze technical eligibility, score past performance, and outline pre-approved corporate templates.
                        </p>
                      </div>

                      {/* SCORE RADIAL BOX / FIT SYNERGY CARD */}
                      <div className="p-4 bg-white border border-gray-200/80 rounded-xl shadow-2xs space-y-3.5">
                        <h5 className="text-[10.5px] font-bold text-gray-700 uppercase tracking-wide">AI Go/No-Go Capability Fit</h5>
                        
                        {analyzingFit ? (
                          <div className="flex flex-col items-center justify-center py-6 space-y-2">
                            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                            <span className="text-[11px] font-medium text-gray-500">Executing Gemini scoring model...</span>
                          </div>
                        ) : fitResult ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-4">
                              {/* Circle radial meter */}
                              <div className="relative w-14 h-14 shrink-0 flex items-center justify-center bg-indigo-50 rounded-full border border-indigo-100">
                                <span className="text-sm font-black text-indigo-700">{fitResult.score}%</span>
                              </div>
                              <div>
                                <span className={`text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-md ${fitResult.recommendation === 'GO' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                                  RECOMMENDED: {fitResult.recommendation}
                                </span>
                                <p className="text-[11px] text-gray-500 leading-snug mt-1">{fitResult.reasoning?.substring(0, 100)}...</p>
                              </div>
                            </div>

                            {/* Risks & Opps clip */}
                            <div className="space-y-1 text-[10.5px] text-gray-600">
                              <p className="font-bold text-indigo-900">Opportunities Identified:</p>
                              <ul className="list-disc pl-4 space-y-0.5 text-[10px] text-gray-600">
                                {fitResult.opportunities?.slice(0, 2).map((o: string, idx: number) => (
                                  <li key={idx}>{o}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handleAnalyzeFit}
                            className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg cursor-pointer transition-all border border-indigo-100/50"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            Generate AI Fit Score
                          </button>
                        )}
                      </div>

                      {/* PROPOSAL DRAFTSMAN TRIGGER CARD */}
                      <div className="p-4 bg-white border border-gray-200/80 rounded-xl shadow-2xs space-y-3.5">
                        <div className="flex items-center justify-between">
                          <h5 className="text-[10.5px] font-bold text-gray-700 uppercase tracking-wide">Proposal Generation Suite</h5>
                          <span className="text-[9px] font-mono font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">16 Docs Sourcing</span>
                        </div>
                        <p className="text-[10.5px] text-gray-500 leading-relaxed">
                          Assemble compliant multi-volume proposal drafts analyzing all 16 Sourcing Documents with empirical GSA & BLS market labor analysis.
                        </p>

                        <button
                          type="button"
                          onClick={() => {
                            setTargetComprehensiveRFX(selectedRFX);
                            setIsComprehensiveModalOpen(true);
                          }}
                          className="w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 rounded-lg cursor-pointer transition-all shadow-sm border border-indigo-400/30"
                        >
                          <Sparkles className="w-4 h-4 text-indigo-200 animate-pulse" />
                          Launch 16-Doc Proposal Studio
                        </button>

                        {generatingResponse ? (
                          <div className="space-y-2 py-1 border-t border-gray-100 pt-2">
                            <div className="flex justify-between items-center text-[10px] text-indigo-600 font-semibold">
                              <span className="flex items-center gap-1">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Drafting Response...
                              </span>
                            </div>
                            <p className="text-[10.5px] text-gray-600 font-medium italic">"{responseProgress}"</p>
                            <div className="h-1.5 w-full bg-gray-50 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-600 rounded-full animate-pulse" style={{ width: '85%' }} />
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handleDraftResponse}
                            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg cursor-pointer transition-all border border-indigo-200/60"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Quick 1-Click Proposal Draft
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center text-xs">
                    <span className="text-gray-400 font-medium">Standard NASPO Threshold Classifications Active</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setSelectedRFX(null); setFitResult(null); }}
                        className="px-4 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg cursor-pointer transition-all"
                      >
                        Close Specifications
                      </button>
                    </div>
                  </div>
                </>
              )}

              {modalTab === 'response_documents' && (
                <div className="flex flex-col h-[530px]">
                  {generatingResponse ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-4">
                      <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-bold text-indigo-900 animate-pulse">"{responseProgress}"</p>
                      <p className="text-xs text-gray-400">Gemini is synthesizing, compiling, and formatting your multi-page compliant RFP response...</p>
                    </div>
                  ) : !currentBid?.generatedContent ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-12 text-center space-y-5">
                      <div className="w-16 h-16 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-indigo-500" />
                      </div>
                      <div className="max-w-md space-y-1.5">
                        <h4 className="font-bold text-gray-950 text-base">No Response Documents Generated Yet</h4>
                        <p className="text-xs text-gray-500 leading-relaxed">
                          Automate proposal writing and instantly produce formal compliant response PDFs including executive summaries, Statement of Work (SOW), line-item schemas, and vendor credentials.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleDraftResponse}
                        className="px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg cursor-pointer transition-all shadow-md flex items-center gap-1.5"
                      >
                        <Sparkles className="w-4 h-4" />
                        Generate Compliant Proposal Draft
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-gray-100 overflow-hidden">
                      {/* Left side: Advanced Workspace Hub (Document & Spreadsheet Viewer) */}
                      <div className="lg:col-span-3 flex flex-col overflow-hidden bg-slate-50/20">
                        {/* Tab Select Bar */}
                        <div className="flex border-b border-gray-200 bg-gray-50/80 px-4 pt-2 gap-1 shrink-0 select-none">
                          <button
                            type="button"
                            onClick={() => setActivePreviewDoc('sow')}
                            className={`px-3 py-1.5 text-[11px] font-extrabold rounded-t-xl border-t border-x flex items-center gap-1.5 transition-all cursor-pointer ${
                              activePreviewDoc === 'sow'
                                ? 'bg-white border-gray-200 text-indigo-700 shadow-3xs'
                                : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-100/30'
                            }`}
                          >
                            <FileText className="w-3.5 h-3.5 text-indigo-600" />
                            Technical_Proposal_SOW.docx
                          </button>
                          <button
                            type="button"
                            onClick={() => setActivePreviewDoc('spreadsheet')}
                            className={`px-3 py-1.5 text-[11px] font-extrabold rounded-t-xl border-t border-x flex items-center gap-1.5 transition-all cursor-pointer ${
                              activePreviewDoc === 'spreadsheet'
                                ? 'bg-white border-gray-200 text-emerald-700 shadow-3xs'
                                : 'border-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-100/30'
                            }`}
                          >
                            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                            Pricing_Breakdown_Sheet.xlsx
                          </button>
                        </div>

                        {/* Document Content Canvas */}
                        <div className="flex-1 p-5 flex flex-col space-y-3 overflow-hidden">
                          {activePreviewDoc === 'sow' ? (
                            <>
                              <div className="flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-1.5">
                                  <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Statement of Work (SOW) Document</h4>
                                  <span className="text-[8px] bg-indigo-50 text-indigo-700 font-bold px-1.5 py-0.2 rounded font-mono border border-indigo-100">Live Word Document View</span>
                                </div>
                                <div className="flex items-center bg-gray-150 p-0.5 rounded-lg border border-gray-200 gap-0.5">
                                  <button
                                    type="button"
                                    onClick={() => setSowMode('preview')}
                                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md flex items-center gap-1 transition-all cursor-pointer ${
                                      sowMode === 'preview' ? 'bg-white text-indigo-900 shadow-3xs' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                                  >
                                    <Eye className="w-3 h-3" />
                                    Live Preview
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setSowMode('edit')}
                                    className={`px-2.5 py-1 text-[10px] font-bold rounded-md flex items-center gap-1 transition-all cursor-pointer ${
                                      sowMode === 'edit' ? 'bg-white text-indigo-900 shadow-3xs' : 'text-gray-500 hover:text-gray-900'
                                    }`}
                                  >
                                    <Edit3 className="w-3 h-3" />
                                    Edit Source
                                  </button>
                                </div>
                              </div>

                              {sowMode === 'preview' ? (
                                <div className="flex-1 bg-slate-100 rounded-xl border border-gray-250/70 overflow-y-auto p-4 flex flex-col items-center scrollbar-thin relative">
                                  {/* PDF Ribbon Controls */}
                                  <div className="sticky top-0 z-10 w-full flex justify-between items-center bg-white/95 backdrop-blur-xs border border-gray-200 rounded-lg px-3 py-1 shadow-2xs mb-4 text-[10px]">
                                    <span className="font-extrabold text-gray-500 flex items-center gap-1.5">
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                                      Adobe PDF / MS Word Document Layout
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-600 font-bold"
                                        title="Zoom Out"
                                      >
                                        <ZoomOut className="w-3.5 h-3.5" />
                                      </button>
                                      <span className="font-mono text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-250">{zoomLevel}%</span>
                                      <button
                                        type="button"
                                        onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}
                                        className="p-1 hover:bg-gray-100 rounded text-gray-600 font-bold"
                                        title="Zoom In"
                                      >
                                        <ZoomIn className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Crisp Paper Canvas */}
                                  <div 
                                    className={cn(
                                      "shadow-lg border rounded-xs p-10 w-full max-w-[500px] min-h-[620px] text-left relative transition-all duration-300",
                                      getBrandFontClass(vendorProfile?.fontFamily),
                                      vendorProfile?.themeMode === 'ambientDark' ? 'bg-slate-900 border-slate-800 text-slate-100 shadow-slate-950/40' :
                                      vendorProfile?.themeMode === 'sepia' ? 'bg-[#faf6ee] border-[#ebd9c3] text-[#433422]' :
                                      vendorProfile?.themeMode === 'cyber' ? 'bg-neutral-950 border-cyan-950 text-emerald-400 font-mono shadow-cyan-950/20' :
                                      'bg-white border-gray-250/70 text-gray-800'
                                    )}
                                    style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top center' }}
                                  >
                                    {/* Formal Cover Letter Block */}
                                    <div className="border-b-2 pb-3 mb-5 flex justify-between items-start" style={{ borderColor: vendorProfile?.mainColor || '#0f172a' }}>
                                      <div className="flex items-center gap-2.5">
                                        {vendorProfile?.logoUrl ? (
                                          <img src={vendorProfile.logoUrl} alt="Logo" className="w-8 h-8 object-contain" />
                                        ) : (
                                          <div 
                                            className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-[10px] shrink-0"
                                            style={{ backgroundColor: vendorProfile?.mainColor || '#4f46e5' }}
                                          >
                                            {vendorProfile?.companyName?.substring(0, 2).toUpperCase() || "RF"}
                                          </div>
                                        )}
                                        <div>
                                          <p className="text-[7.5px] tracking-widest font-black uppercase" style={{ color: vendorProfile?.mainColor || '#4f46e5' }}>OFFICIAL PROCUREMENT AGREEMENT</p>
                                          <h2 className="text-xs font-black uppercase tracking-tight font-serif mt-0.5" style={{ color: vendorProfile?.themeMode === 'ambientDark' ? '#f1f5f9' : '#0f172a' }}>TECHNICAL PROPOSAL & SOW</h2>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-[8px] font-mono text-gray-400">SOL_ID: {selectedRFX?.solicitationId}</p>
                                        <p className="text-[8.5px] font-bold text-slate-800 mt-0.5" style={{ color: vendorProfile?.themeMode === 'ambientDark' ? '#94a3b8' : '#334155' }}>STATE OF {selectedRFX?.state}</p>
                                      </div>
                                    </div>

                                    {/* Title block */}
                                    <div 
                                      className="p-3.5 rounded-r-lg mb-5 border-l-3"
                                      style={{ 
                                        borderLeftColor: vendorProfile?.mainColor || '#4f46e5',
                                        backgroundColor: vendorProfile?.themeMode === 'ambientDark' ? '#1e293b' :
                                                         vendorProfile?.themeMode === 'sepia' ? '#eedfcb' :
                                                         vendorProfile?.themeMode === 'cyber' ? '#0a190f' : '#f8fafc'
                                      }}
                                    >
                                      <h3 className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: vendorProfile?.themeMode === 'cyber' ? '#10b981' : '#0f172a' }}>Statement of Work Proposal</h3>
                                      <p className="text-[10px] mt-0.5 leading-relaxed" style={{ color: vendorProfile?.themeMode === 'ambientDark' ? '#cbd5e1' : '#475569' }}>
                                        Prepared for the **{selectedRFX?.agency || 'Procurement Office'}** responding to solicitation **{selectedRFX?.solicitationId || 'RFP'}** for *{selectedRFX?.title}*.
                                      </p>
                                      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t text-[9px] font-medium font-mono" style={{ borderColor: vendorProfile?.themeMode === 'ambientDark' ? '#334155' : '#e2e8f0' }}>
                                        <div>
                                          <span className="block text-[7.5px] text-gray-400 font-bold">CONTRACTOR VENDOR</span>
                                          <span className="font-bold" style={{ color: vendorProfile?.mainColor || '#0f172a' }}>
                                            {vendorProfile?.companyName || "Quantum AI Solutions Inc."}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="block text-[7.5px] text-gray-400 font-bold">STATUS</span>
                                          Compliant Draft Prepared
                                        </div>
                                      </div>
                                    </div>

                                    {/* Markdown render */}
                                    <div className="prose prose-slate max-w-none text-xs leading-relaxed space-y-2">
                                      <ReactMarkdown
                                        components={{
                                          h1: ({ children }) => <h1 className="text-[11.5px] font-black border-b pb-1 mt-4 mb-2 uppercase font-serif tracking-tight" style={{ color: vendorProfile?.mainColor || '#0f172a', borderColor: '#e2e8f0' }}>{children}</h1>,
                                          h2: ({ children }) => <h2 className="text-[10.5px] font-bold mt-3.5 mb-1 uppercase tracking-wide" style={{ color: vendorProfile?.mainColor || '#1e3a8a' }}>{children}</h2>,
                                          h3: ({ children }) => <h3 className="text-[10px] font-black mt-2.5 mb-1" style={{ color: vendorProfile?.themeMode === 'ambientDark' ? '#f1f5f9' : '#1e293b' }}>{children}</h3>,
                                          p: ({ children }) => <p className="text-[10px] leading-relaxed mb-2 font-medium" style={{ color: vendorProfile?.themeMode === 'ambientDark' ? '#cbd5e1' : '#334155' }}>{children}</p>,
                                          ul: ({ children }) => <ul className="list-disc pl-4 space-y-1 mb-2 text-[10px] font-medium">{children}</ul>,
                                          ol: ({ children }) => <ol className="list-decimal pl-4 space-y-1 mb-2 text-[10px] font-medium">{children}</ol>,
                                          li: ({ children }) => <li className="leading-relaxed" style={{ color: vendorProfile?.themeMode === 'ambientDark' ? '#cbd5e1' : '#475569' }}>{children}</li>,
                                          strong: ({ children }) => <strong className="font-extrabold" style={{ color: vendorProfile?.mainColor || '#0f172a' }}>{children}</strong>,
                                          table: ({ children }) => (
                                            <div className="my-3 overflow-hidden border border-gray-200 rounded-lg">
                                              <table className="w-full text-[9px] border-collapse bg-white">{children}</table>
                                            </div>
                                          ),
                                          thead: ({ children }) => <thead className="bg-slate-50 border-b border-gray-200 font-bold text-slate-800">{children}</thead>,
                                          tbody: ({ children }) => <tbody className="divide-y divide-gray-100">{children}</tbody>,
                                          tr: ({ children }) => <tr className="divide-x divide-gray-100">{children}</tr>,
                                          th: ({ children }) => <th className="px-1.5 py-1 text-left font-black">{children}</th>,
                                          td: ({ children }) => <td className="px-1.5 py-1 text-slate-600">{children}</td>,
                                          blockquote: ({ children }) => (
                                            <blockquote 
                                              className="border-l-2 pl-3 italic p-1.5 rounded" 
                                              style={{ 
                                                borderLeftColor: vendorProfile?.mainColor || '#4f46e5',
                                                backgroundColor: vendorProfile?.themeMode === 'ambientDark' ? '#1e293b' : '#f8fafc'
                                              }}
                                            >
                                              {children}
                                            </blockquote>
                                          )
                                        }}
                                      >
                                        {editedProposal || currentBid?.generatedContent || "SOW Proposal compiled."}
                                      </ReactMarkdown>
                                    </div>

                                    {/* Document Footer */}
                                    <div className="border-t pt-3 mt-8 flex justify-between items-center text-[7.5px] font-mono text-slate-400 select-none" style={{ borderColor: vendorProfile?.themeMode === 'ambientDark' ? '#334155' : '#e2e8f0' }}>
                                      <span>CONFIDENTIAL RESPONDENT • {vendorProfile?.companyName?.toUpperCase() || "QUANTUM AI SOLUTIONS INC."}</span>
                                      <span>Page 1 of 3</span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <textarea
                                  value={editedProposal}
                                  onChange={(e) => setEditedProposal(e.target.value)}
                                  className="flex-1 w-full p-4 border border-gray-200 rounded-xl text-xs font-mono leading-relaxed bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none scrollbar-thin"
                                  placeholder="Paste or edit the response text here..."
                                />
                              )}
                            </>
                          ) : (
                            <>
                              <div className="flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-1.5">
                                  <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Proposal Costing Spreadsheet</h4>
                                  <span className="text-[8px] bg-emerald-50 text-emerald-700 font-bold px-1.5 py-0.2 rounded font-mono border border-emerald-100 animate-pulse">● Excel Live Viewer Active</span>
                                </div>
                                <span className="text-[10px] text-slate-400 font-mono">Formulas & Live Calculations Enabled</span>
                              </div>

                              {/* Embedding Spreadsheet right into the viewer */}
                              <div className="flex-1 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-left font-sans flex flex-col">
                                {/* Google Sheets Title & Ribbon */}
                                <div className="bg-slate-50 border-b border-slate-200 p-2 flex flex-col gap-1 px-3">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="p-1.5 text-white rounded-md transition-all shrink-0"
                                        style={{ backgroundColor: vendorProfile?.mainColor || '#059669' }}
                                      >
                                        <FileSpreadsheet className="w-3.5 h-3.5" />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-xs font-black text-slate-800 font-mono">
                                            {selectedRFX?.solicitationId || 'RFP'}_Bid_Costing.xlsx
                                          </span>
                                          <span 
                                            className="font-mono text-[8px] px-1 py-0.2 rounded font-semibold text-white shrink-0"
                                            style={{ backgroundColor: vendorProfile?.mainColor || '#10b981' }}
                                          >
                                            Live Connected
                                          </span>
                                        </div>
                                        <div className="flex gap-2.5 text-[9.5px] text-slate-400 font-medium">
                                          <span className="hover:text-slate-600 cursor-pointer">File</span>
                                          <span className="hover:text-slate-600 cursor-pointer">Edit</span>
                                          <span className="hover:text-slate-600 cursor-pointer font-bold text-emerald-700">Format</span>
                                          <span className="hover:text-slate-600 cursor-pointer font-bold text-emerald-700">Data</span>
                                          <span className="hover:text-slate-600 cursor-pointer">Tools</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Excel Toolbar */}
                                <div className="bg-slate-50 border-b border-slate-200 px-3 py-1 flex items-center justify-between gap-2 overflow-x-auto select-none">
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500" title="Undo"><Undo className="w-3 h-3" /></button>
                                    <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500" title="Redo"><Redo className="w-3 h-3" /></button>
                                    <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500" title="Print"><Printer className="w-3 h-3" /></button>
                                    <div className="h-4 w-px bg-slate-200 mx-1" />
                                    <span className="text-[10px] text-slate-500 font-mono font-medium bg-white px-1.5 py-0.5 border border-slate-200 rounded">Arial</span>
                                    <div className="h-4 w-px bg-slate-200 mx-1" />
                                    <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-700 font-black text-[10px] font-mono" title="Bold">B</button>
                                    <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-700 italic text-[10px] font-mono" title="Italic">I</button>
                                    <button type="button" className="p-1 hover:bg-slate-200 rounded text-slate-500 bg-emerald-100" title="Borders"><LayoutTemplate className="w-3 h-3 text-emerald-700" /></button>
                                  </div>

                                  {/* Active Cell & Formula Bar */}
                                  <div className="flex-1 flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-0.5">
                                    <div className="text-[10px] text-emerald-700 font-mono font-bold border-r border-slate-200 pr-2 select-none">
                                      {selectedCell ? `${selectedCell.field === 'price' ? 'E' : selectedCell.field === 'qty' ? 'D' : 'B'}${selectedCell.row + 2}` : 'A1'}
                                    </div>
                                    <span className="text-[10px] text-slate-400 italic font-mono select-none">fx</span>
                                    <input
                                      type="text"
                                      value={
                                        selectedCell 
                                          ? selectedCell.field === 'price' 
                                            ? spreadsheetRows[selectedCell.row]?.price || ''
                                            : selectedCell.field === 'qty'
                                              ? spreadsheetRows[selectedCell.row]?.qty || ''
                                              : spreadsheetRows[selectedCell.row]?.item || ''
                                          : ''
                                      }
                                      onChange={(e) => {
                                        if (!selectedCell) return;
                                        const val = e.target.value;
                                        const updated = [...spreadsheetRows];
                                        if (selectedCell.field === 'price') {
                                          const p = parseFloat(val) || 0;
                                          updated[selectedCell.row].price = p;
                                          updated[selectedCell.row].total = updated[selectedCell.row].qty * p;
                                        } else if (selectedCell.field === 'qty') {
                                          const q = parseInt(val) || 0;
                                          updated[selectedCell.row].qty = q;
                                          updated[selectedCell.row].total = q * updated[selectedCell.row].price;
                                        } else {
                                          updated[selectedCell.row].item = val;
                                        }
                                        setSpreadsheetRows(updated);
                                      }}
                                      className="flex-1 bg-transparent border-none outline-none text-[10.5px] font-mono text-slate-800 p-0 focus:ring-0 focus:outline-none"
                                      placeholder="Cell Value"
                                    />
                                  </div>
                                </div>

                                {/* Grid Table */}
                                <div className="flex-1 overflow-y-auto scrollbar-thin">
                                  <table className="w-full border-collapse text-[10.5px] font-mono text-slate-700">
                                    <thead className="sticky top-0 bg-slate-50 z-10 font-bold text-center border-b border-slate-200 bg-slate-100 text-[9px] text-slate-500">
                                      <tr className="divide-x divide-slate-200">
                                        <th className="w-8 bg-slate-200/50" />
                                        <th className="px-3 py-1 text-left w-14">A (Line)</th>
                                        <th className="px-3 py-1 text-left min-w-[200px]">B (Commodity Item Description)</th>
                                        <th className="px-2 py-1 w-12">C (Unit)</th>
                                        <th className="px-3 py-1 text-right w-14">D (Qty)</th>
                                        <th className="px-3 py-1 text-right w-24">E (Unit Price)</th>
                                        <th className="px-3 py-1 text-right bg-emerald-50/50 text-emerald-900 font-bold w-28 border-l border-slate-200">F (Total Cost)</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-150">
                                      {spreadsheetRows.map((row, rIdx) => (
                                        <tr key={rIdx} className="hover:bg-slate-50/50 group/row divide-x divide-slate-150">
                                          <td className="w-8 bg-slate-100/80 text-center text-[9px] text-slate-400 font-medium select-none border-r border-slate-200">
                                            {rIdx + 2}
                                          </td>
                                          <td className="px-3 py-1.5 text-slate-400 font-bold select-none text-[10px]">{row.line}</td>
                                          <td 
                                            onClick={() => setSelectedCell({ row: rIdx, col: 1, field: 'item' })}
                                            className={`px-3 py-1.5 truncate max-w-[200px] cursor-cell transition-all font-sans text-xs ${
                                              selectedCell?.row === rIdx && selectedCell?.field === 'item'
                                                ? 'outline-2 outline-emerald-500 bg-emerald-50/20 font-semibold text-slate-900'
                                                : 'text-slate-800'
                                            }`}
                                          >
                                            {row.item}
                                          </td>
                                          <td className="px-2 py-1.5 text-center text-slate-500 text-[10px] select-none">{row.unit}</td>
                                          <td 
                                            onClick={() => setSelectedCell({ row: rIdx, col: 3, field: 'qty' })}
                                            className={`px-3 py-1.5 text-right cursor-cell transition-all ${
                                              selectedCell?.row === rIdx && selectedCell?.field === 'qty'
                                                ? 'outline-2 outline-emerald-500 bg-emerald-50/20 font-black text-slate-900'
                                                : 'text-slate-600 font-medium'
                                            }`}
                                          >
                                            {row.qty}
                                          </td>
                                          <td 
                                            onClick={() => setSelectedCell({ row: rIdx, col: 4, field: 'price' })}
                                            className={`px-3 py-1.5 text-right cursor-cell transition-all relative ${
                                              selectedCell?.row === rIdx && selectedCell?.field === 'price'
                                                ? 'outline-2 outline-emerald-500 bg-emerald-50/25 font-black text-slate-900'
                                                : 'text-slate-900 hover:bg-slate-100/50 font-bold'
                                            }`}
                                          >
                                            <div className="flex items-center justify-end">
                                              <span className="text-[9px] text-slate-400 mr-0.5">$</span>
                                              <input
                                                type="number"
                                                value={row.price}
                                                onChange={(e) => {
                                                  const updated = [...spreadsheetRows];
                                                  const p = parseFloat(e.target.value) || 0;
                                                  updated[rIdx].price = p;
                                                  updated[rIdx].total = updated[rIdx].qty * p;
                                                  setSpreadsheetRows(updated);
                                                }}
                                                className="w-14 bg-transparent text-right outline-none border-none p-0 focus:ring-0 font-bold"
                                              />
                                            </div>
                                          </td>
                                          <td className="px-3 py-1.5 text-right font-bold text-slate-900 bg-emerald-50/20">
                                            ${row.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </td>
                                        </tr>
                                      ))}
                                      
                                      <tr 
                                        className="divide-x divide-slate-150 font-bold border-t-2 border-double sticky bottom-0 text-white"
                                        style={{ 
                                          backgroundColor: vendorProfile?.mainColor || '#047857',
                                          borderColor: vendorProfile?.mainColor ? `${vendorProfile.mainColor}80` : '#cbd5e1'
                                        }}
                                      >
                                        <td className="w-8 bg-slate-200/50 text-center text-[9px] text-slate-500 select-none">
                                          {spreadsheetRows.length + 2}
                                        </td>
                                        <td className="px-3 py-2 text-white/80 text-[10px]">TOTAL</td>
                                        <td className="px-3 py-2 text-white font-sans text-xs">
                                          Compiled SOW Pricing Summation (Formula: SUM(F2:F{spreadsheetRows.length + 1}))
                                        </td>
                                        <td className="px-2 py-2" />
                                        <td className="px-3 py-2 text-right text-white">{spreadsheetRows.reduce((acc, r) => acc + r.qty, 0)}</td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2 text-right text-white font-extrabold text-[11px] underline decoration-double">
                                          ${spreadsheetRows.reduce((acc, r) => acc + r.total, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>

                                {/* Sheets Tabs Footer */}
                                <div className="bg-slate-100 border-t border-slate-200 px-4 py-1 flex items-center gap-1.5 text-[9.5px] font-bold select-none text-slate-500 shrink-0">
                                  <span className="text-slate-400 mr-1 shrink-0">➕ ☰</span>
                                  <div 
                                    className="bg-white border-x border-t border-slate-200 rounded-t px-3 py-1 flex items-center gap-1.5 -mb-1"
                                    style={{ color: vendorProfile?.mainColor || '#047857' }}
                                  >
                                    <span 
                                      className="w-1.5 h-1.5 rounded-full" 
                                      style={{ backgroundColor: vendorProfile?.mainColor || '#10b981' }}
                                    />
                                    Cost Schedule (Core Pricing)
                                  </div>
                                  <div className="hover:bg-slate-200 px-3 py-1 rounded-t cursor-pointer">
                                    SLA Labor Rates
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Save Changes and Delete Proposal button bar */}
                          <div className="flex items-center justify-end gap-2 shrink-0 pt-1">
                            {currentBid && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!user?.uid || !selectedRFX?.solicitationId) return;
                                  if (!window.confirm(`Are you sure you want to delete this proposal response for "${selectedRFX.solicitationId}"?`)) return;
                                  try {
                                    const bidId = `${user.uid}_${selectedRFX.solicitationId}`;
                                    await deleteDoc(doc(db, 'bids', bidId));
                                    setEditedProposal('');
                                    addToast({
                                      title: 'Proposal Response Deleted',
                                      message: 'The proposal response has been permanently deleted from your RFX responses.',
                                      type: 'info'
                                    });
                                  } catch (err: any) {
                                    console.error("Failed to delete proposal:", err);
                                    addToast({ title: 'Error', message: 'Failed to delete proposal.', type: 'danger' });
                                  }
                                }}
                                className="px-3 py-1.5 text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200/50 rounded-lg cursor-pointer transition-all flex items-center gap-1.5"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Delete Proposal
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  if (!user?.uid) {
                                    addToast({ title: 'Sign in required', message: 'Please sign in to save your proposal changes.', type: 'danger' });
                                    return;
                                  }
                                  const bidId = `${user.uid}_${selectedRFX.solicitationId}`;
                                  await updateDoc(doc(db, 'bids', bidId), {
                                    generatedContent: editedProposal,
                                    updatedAt: serverTimestamp()
                                  });
                                  addToast({
                                    title: 'Proposal & Pricing Saved',
                                    message: 'Your modifications have been saved in Firestore and will reflect in your downloaded PDF.',
                                    type: 'success'
                                  });
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                              className="px-4 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200/50 rounded-lg cursor-pointer transition-all flex items-center gap-1.5"
                            >
                              <Save className="w-3.5 h-3.5" />
                              Save Changes
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Right side: Artifacts list & Submit action */}
                      <div className="lg:col-span-2 p-6 overflow-y-auto space-y-5 bg-slate-50/50 scrollbar-thin flex flex-col">
                        <div className="flex-1 space-y-4">
                          <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Generated Response Assets & Deliverables</h4>
                          
                          {/* SOW PDF Item Card */}
                          <div 
                            onClick={() => setActivePreviewDoc('sow')}
                            className={`p-3.5 border rounded-xl shadow-3xs flex items-center justify-between gap-3 cursor-pointer transition-all ${
                              activePreviewDoc === 'sow' 
                                ? 'bg-indigo-50/45 border-indigo-200 ring-2 ring-indigo-600/10' 
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0 font-bold text-xs font-mono">
                                PDF
                              </div>
                              <div className="min-w-0">
                                <h5 className="text-xs font-black text-gray-900 truncate">Technical_&_Cost_Proposal.pdf</h5>
                                <p className="text-[9.5px] text-gray-400 mt-0.5">SOW Document Proposal • Zoomable Page</p>
                              </div>
                            </div>
                            <span className="bg-indigo-50 text-indigo-700 text-[8px] px-2 py-0.5 rounded-md font-bold shrink-0 border border-indigo-100">Ready</span>
                          </div>

                          {/* Cost XLSX Item Card */}
                          <div 
                            onClick={() => setActivePreviewDoc('spreadsheet')}
                            className={`p-3.5 border rounded-xl shadow-3xs flex items-center justify-between gap-3 cursor-pointer transition-all ${
                              activePreviewDoc === 'spreadsheet' 
                                ? 'bg-emerald-50/45 border-emerald-200 ring-2 ring-emerald-600/10' 
                                : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 font-bold text-xs font-mono">
                                XLSX
                              </div>
                              <div className="min-w-0">
                                <h5 className="text-xs font-black text-gray-900 truncate">Pricing_Breakdown_Sheet.xlsx</h5>
                                <p className="text-[9.5px] text-gray-400 mt-0.5">Interactive Cost Sheet • Formulas Enabled</p>
                              </div>
                            </div>
                            <span className="bg-emerald-50 text-emerald-700 text-[8px] px-2 py-0.5 rounded-md font-bold shrink-0 border border-emerald-100">Living</span>
                          </div>

                          <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-3xs space-y-2.5">
                            <h5 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Document Quality Checks</h5>
                            <div className="space-y-1.5 text-[10.5px] text-slate-600 font-medium">
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <span>Federal & State compliance structures met</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <span>Vendor credentials & contacts fully populated</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <span>Deliverable compliance table verified</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons inside right column */}
                        <div className="space-y-2 shrink-0 pt-4 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={() => {
                              generateProposalPdf(editedProposal || currentBid.generatedContent, {
                                title: selectedRFX.title,
                                agency: selectedRFX.agency,
                                solicitationId: selectedRFX.solicitationId,
                                vendorName: "Quantum AI Solutions Inc.",
                                vendorAddress: "123 Quantum Way, Suite 100",
                                vendorEmail: "isa.hunter@quantumaiinc.solutions",
                                dueDate: selectedRFX.dueDate,
                                state: selectedRFX.state
                              });
                              addToast({
                                title: 'PDF Proposal Exported',
                                message: 'Your formatted multi-page proposal PDF has been generated and downloaded.',
                                type: 'success'
                              });
                            }}
                            className="w-full py-2 px-4 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg cursor-pointer transition-all shadow-md flex items-center justify-center gap-1.5"
                          >
                            Download Proposal PDF
                          </button>
                          
                          <button
                            type="button"
                            onClick={async () => {
                              if (!user?.uid) {
                                addToast({ title: 'Sign in required', message: 'Please sign in before submitting this proposal.', type: 'danger' });
                                return;
                              }
                              try {
                                const bidId = `${user.uid}_${selectedRFX.solicitationId}`;
                                await updateDoc(doc(db, 'bids', bidId), {
                                  status: 'submitted',
                                  updatedAt: serverTimestamp()
                                });
                                addToast({
                                  title: 'Bid Response Submitted',
                                  message: 'Your official proposal has been marked as submitted and dispatched to procurement.',
                                  type: 'success'
                                });
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            className={`w-full py-2 px-4 text-xs font-bold rounded-lg cursor-pointer transition-all flex items-center justify-center gap-1.5 border ${
                              currentBid?.status === 'submitted'
                                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                : "bg-emerald-600 text-white hover:bg-emerald-700 border-transparent shadow-md"
                            }`}
                          >
                            <Check className="w-4 h-4" />
                            {currentBid?.status === 'submitted' ? 'Proposal Submitted' : 'Proposal'}
                          </button>

                          <button
                            type="button"
                            onClick={handleDraftResponse}
                            className="w-full py-1.5 text-[11px] font-bold text-gray-500 hover:text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg cursor-pointer transition-all"
                          >
                            Regenerate with AI
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* COMPREHENSIVE 16-DOCUMENT PROPOSAL STUDIO MODAL */}
      {targetComprehensiveRFX && (
        <ComprehensiveProposalStudioModal
          isOpen={isComprehensiveModalOpen}
          onClose={() => {
            setIsComprehensiveModalOpen(false);
            setTargetComprehensiveRFX(null);
          }}
          solicitation={{
            solicitationId: targetComprehensiveRFX.solicitationId,
            title: targetComprehensiveRFX.title,
            agency: targetComprehensiveRFX.agency,
            state: targetComprehensiveRFX.state,
            pricing: targetComprehensiveRFX.pricing,
            description: targetComprehensiveRFX.description
          }}
          vendorProfile={vendorProfile}
          onSavedToDatabase={(draft) => {
            if (user) {
              const bidId = `${user.uid}_${getSafeSolicitationDocId(targetComprehensiveRFX.solicitationId)}`;
              const bidData = ensureFirestoreSafeSize({
                solicitationId: targetComprehensiveRFX.solicitationId,
                vendorUid: user.uid,
                status: 'draft',
                pricing: draft.volume4CostProposal.finalProposedPrice,
                analysis: {
                  score: Math.round(draft.overallComplianceScore),
                  recommendation: 'GO',
                  reasoning: 'Synthesized via 16-Document RFX Sourcing Analyzer & Market Cost Model.'
                },
                generatedContent: `# VOLUME I: TRANSMITTAL & EXECUTIVE SUMMARY\n\n${draft.volume1ExecutiveSummary.transmittalLetter}\n\n${draft.volume1ExecutiveSummary.executiveSummary}\n\n# VOLUME II: TECHNICAL APPROACH\n\n${draft.volume2TechnicalApproach.technicalMethodologyAndArchitecture}`,
                comprehensiveProposal: draft,
                updatedAt: serverTimestamp()
              });

              setDoc(doc(db, 'bids', bidId), bidData, { merge: true })
                .then(() => {
                  addToast({
                    title: 'Comprehensive Proposal Saved',
                    message: `Multi-volume draft for ${targetComprehensiveRFX.solicitationId} synced to RFX Bid Vault.`,
                    type: 'success'
                  });
                })
                .catch((err) => {
                  handleFirestoreError(err, OperationType.WRITE, `bids/${bidId}`);
                });
            }
          }}
        />
      )}
    </div>
  );
};
